import { Badge, Button } from "flowbite-react";
import { IoMdSearch, IoMdClose } from "react-icons/io";
import { MdTune } from "react-icons/md";
import {
  HEALTH_OPTIONS,
  PAGE_SIZE_OPTIONS,
  PLAN_OPTIONS,
  SORT_OPTIONS,
  STATUS_OPTIONS,
} from "./customerHelpers";

const QUICK_FILTERS = [
  { key: "all", label: "Todos", filters: { status: "", health: "", onlyNew: false } },
  { key: "active", label: "Ativos", filters: { status: "ACTIVE", health: "", onlyNew: false } },
  { key: "expiring", label: "Expirando", filters: { status: "EXPIRING", health: "", onlyNew: false } },
  { key: "expired", label: "Expirados", filters: { status: "EXPIRED", health: "", onlyNew: false } },
  { key: "using", label: "Usando", filters: { status: "", health: "ACTIVE", onlyNew: false } },
  { key: "atRisk", label: "Em risco", filters: { status: "", health: "AT_RISK", onlyNew: false } },
  { key: "neverUsed", label: "Nunca usou", filters: { status: "", health: "NEVER_USED", onlyNew: false } },
  { key: "new", label: "Novos (30d)", filters: { status: "", health: "", onlyNew: true } },
];

const fieldClass =
  "block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 transition placeholder:text-slate-400 focus:border-pink-500 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400";

const labelClass =
  "block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400";

const isSameQuickFilter = (filters, candidate) =>
  (filters.status || "") === candidate.status &&
  (filters.health || "") === candidate.health &&
  Boolean(filters.onlyNew) === Boolean(candidate.onlyNew);

const ActiveChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">
    {label}
    <button
      type="button"
      onClick={onRemove}
      className="text-slate-400 transition hover:text-rose-500"
      aria-label={`Remover filtro ${label}`}
    >
      <IoMdClose />
    </button>
  </span>
);

/**
 * Busca + filtros rápidos + filtros avançados. Todos os filtros são
 * controlados pelo componente pai (estado único), o que mantém a URL de
 * consulta e os KPIs sempre coerentes.
 */
const CustomerFiltersBar = ({
  filters,
  owners = [],
  // Rótulo e descrição de cada estágio vêm do servidor (GET /crm/stages), e
  // não de uma lista no front: a regra que define os estágios é de negócio, e
  // duplicá-la aqui faria a tela descrever um recorte diferente do aplicado.
  stages = [],
  canFilterByOwner = false,
  searchTerm,
  onSearchTermChange,
  onChange,
  onReset,
  showAdvanced,
  onToggleAdvanced,
  activeFilterCount,
}) => {
  const sortValue = `${filters.sortBy}:${filters.sortOrder}`;

  const activeChips = [];

  if (filters.planId) {
    activeChips.push({
      label: `Plano: ${PLAN_OPTIONS.find((option) => option.value === filters.planId)?.label}`,
      onRemove: () => onChange({ planId: "" }),
    });
  }

  if (filters.status) {
    activeChips.push({
      label: STATUS_OPTIONS.find((option) => option.value === filters.status)?.label,
      onRemove: () => onChange({ status: "" }),
    });
  }

  if (filters.health) {
    activeChips.push({
      label: HEALTH_OPTIONS.find((option) => option.value === filters.health)?.label,
      onRemove: () => onChange({ health: "" }),
    });
  }

  if (filters.stage) {
    activeChips.push({
      label: `Estágio: ${
        stages.find((stage) => stage.id === filters.stage)?.label ||
        filters.stage
      }`,
      onRemove: () => onChange({ stage: "" }),
    });
  }

  if (filters.ownerUserId) {
    const ownerLabel =
      filters.ownerUserId === "none"
        ? "Sem responsável"
        : owners.find(
            (owner) => String(owner.id) === String(filters.ownerUserId)
          )?.name || "Responsável";

    activeChips.push({
      label: `Responsável: ${ownerLabel}`,
      onRemove: () => onChange({ ownerUserId: "" }),
    });
  }

  if (filters.onlyNew) {
    activeChips.push({
      label: "Cadastrados nos últimos 30 dias",
      onRemove: () => onChange({ onlyNew: false }),
    });
  }

  if (filters.onlyOverSeatLimit) {
    activeChips.push({
      label: "Acima do limite de usuários",
      onRemove: () => onChange({ onlyOverSeatLimit: false }),
    });
  }

  if (filters.registrationStartDate || filters.registrationEndDate) {
    activeChips.push({
      label: `Cadastro: ${filters.registrationStartDate || "início"} → ${
        filters.registrationEndDate || "hoje"
      }`,
      onRemove: () =>
        onChange({ registrationStartDate: "", registrationEndDate: "" }),
    });
  }

  if (searchTerm) {
    activeChips.push({
      label: `Busca: "${searchTerm}"`,
      onRemove: () => onSearchTermChange(""),
    });
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="flex flex-1 items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 focus-within:border-pink-400">
          <IoMdSearch className="shrink-0 text-slate-400" size={18} />
          <input
            type="text"
            placeholder="Buscar por nome, e-mail, telefone, documento, cupom ou #id"
            className="w-full border-0 bg-transparent p-0 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-0"
            value={searchTerm}
            onChange={(event) => onSearchTermChange(event.target.value)}
          />
          {searchTerm ? (
            <button
              type="button"
              onClick={() => onSearchTermChange("")}
              className="text-slate-400 transition hover:text-slate-700"
              aria-label="Limpar busca"
            >
              <IoMdClose />
            </button>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            color={showAdvanced ? "dark" : "light"}
            onClick={onToggleAdvanced}
          >
            <MdTune className="mr-2 h-4 w-4" />
            Filtros
            {activeFilterCount > 0 ? (
              <Badge color="failure" className="ml-2 rounded-full px-2">
                {activeFilterCount}
              </Badge>
            ) : null}
          </Button>
          {activeFilterCount > 0 || searchTerm ? (
            <Button size="sm" color="light" onClick={onReset}>
              Limpar
            </Button>
          ) : null}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {QUICK_FILTERS.map((quickFilter) => {
          const isActive = isSameQuickFilter(filters, quickFilter.filters);

          return (
            <button
              key={quickFilter.key}
              type="button"
              onClick={() => onChange(quickFilter.filters)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                isActive
                  ? "border-pink-500 bg-pink-50 text-pink-600"
                  : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700"
              }`}
            >
              {quickFilter.label}
            </button>
          );
        })}
      </div>

      {showAdvanced ? (
        <div className="mt-4 grid grid-cols-1 gap-3 border-t border-slate-100 pt-4 sm:grid-cols-2 xl:grid-cols-3">
          <div>
            <label className={labelClass} htmlFor="filter-stage">
              Estágio no ciclo de vida
            </label>
            <select
              id="filter-stage"
              className={`mt-1 ${fieldClass}`}
              value={filters.stage}
              onChange={(event) => onChange({ stage: event.target.value })}
            >
              <option value="">Todos os estágios</option>
              {stages.map((stage) => (
                <option key={stage.id} value={stage.id} title={stage.description}>
                  {stage.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass} htmlFor="filter-plan">
              Plano
            </label>
            <select
              id="filter-plan"
              className={`mt-1 ${fieldClass}`}
              value={filters.planId}
              onChange={(event) => onChange({ planId: event.target.value })}
            >
              {PLAN_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass} htmlFor="filter-status">
              Situação do plano
            </label>
            <select
              id="filter-status"
              className={`mt-1 ${fieldClass}`}
              value={filters.status}
              onChange={(event) => onChange({ status: event.target.value })}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass} htmlFor="filter-health">
              Engajamento
            </label>
            <select
              id="filter-health"
              className={`mt-1 ${fieldClass}`}
              value={filters.health}
              onChange={(event) => onChange({ health: event.target.value })}
            >
              {HEALTH_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {canFilterByOwner ? (
            <div>
              <label className={labelClass} htmlFor="filter-owner">
                Responsável
              </label>
              <select
                id="filter-owner"
                className={`mt-1 ${fieldClass}`}
                value={filters.ownerUserId}
                onChange={(event) =>
                  onChange({ ownerUserId: event.target.value })
                }
              >
                <option value="">Todos os responsáveis</option>
                <option value="none">Sem responsável</option>
                {owners.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.name} ({owner.clientCount})
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div>
            <label className={labelClass} htmlFor="filter-start">
              Cadastro (início)
            </label>
            <input
              id="filter-start"
              type="date"
              className={`mt-1 ${fieldClass}`}
              value={filters.registrationStartDate}
              onChange={(event) =>
                onChange({ registrationStartDate: event.target.value })
              }
            />
          </div>

          <div>
            <label className={labelClass} htmlFor="filter-end">
              Cadastro (fim)
            </label>
            <input
              id="filter-end"
              type="date"
              className={`mt-1 ${fieldClass}`}
              value={filters.registrationEndDate}
              onChange={(event) =>
                onChange({ registrationEndDate: event.target.value })
              }
            />
          </div>

          <div>
            <label className={labelClass} htmlFor="filter-sort">
              Ordenar por
            </label>
            <select
              id="filter-sort"
              className={`mt-1 ${fieldClass}`}
              value={sortValue}
              onChange={(event) => {
                const [sortBy, sortOrder] = event.target.value.split(":");
                onChange({ sortBy, sortOrder });
              }}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass} htmlFor="filter-page-size">
              Itens por página
            </label>
            <select
              id="filter-page-size"
              className={`mt-1 ${fieldClass}`}
              value={filters.itemsPerPage}
              onChange={(event) =>
                onChange({ itemsPerPage: Number(event.target.value) })
              }
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} por página
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-pink-600 focus:ring-pink-500"
                checked={Boolean(filters.onlyOverSeatLimit)}
                onChange={(event) =>
                  onChange({ onlyOverSeatLimit: event.target.checked })
                }
              />
              Apenas acima do limite de usuários
            </label>
          </div>
        </div>
      ) : null}

      {activeChips.length ? (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
          <span className="text-xs uppercase tracking-[0.2em] text-slate-400">
            Filtros
          </span>
          {activeChips.map((chip) => (
            <ActiveChip key={chip.label} label={chip.label} onRemove={chip.onRemove} />
          ))}
        </div>
      ) : null}
    </section>
  );
};

export default CustomerFiltersBar;
